import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Wrench, AlertCircle, Search, CheckCircle2, History, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Input } from '../../components/ui';
import { cn } from '../../lib/utils';
import { getHeroClass } from '../../lib/design-system';
import { useProperty } from '../../contexts/PropertyContext';

import {
  getMaintenanceSchedule,
  updateScheduledTask,
  ScheduledTask,
} from '../../services/maintenance.api';


import { MaintenanceSummaryCards } from '../../components/maintenance/MaintenanceSummaryCards';
import { MaintenanceSection } from '../../components/maintenance/MaintenanceSection';
import { SkipReasonModal, SkipReasonValue } from '../../components/maintenance/SkipReasonModal';
import { DIYMaintenanceDrawer } from '../../components/DIYMaintenanceDrawer';
import { AIDrawer } from '../../components/AIDrawer';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

function sortByPriority(tasks: ScheduledTask[]): ScheduledTask[] {
  return [...tasks].sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority ?? 'low'] ?? 2;
    const pb = PRIORITY_ORDER[b.priority ?? 'low'] ?? 2;
    if (pa !== pb) return pa - pb;
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });
}

function groupByStatus(tasks: ScheduledTask[]) {
  return {
    overdue: sortByPriority(tasks.filter((t) => t.status === 'overdue')),
    upcoming: sortByPriority(tasks.filter((t) => t.status === 'upcoming')),
    snoozed: tasks.filter((t) => t.status === 'snoozed'),
    completed: tasks.filter((t) => t.status === 'completed'),
    skipped: tasks.filter((t) => t.status === 'skipped'),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MaintenancePage() {
  const { t } = useTranslation('maintenance');
  const { currentProperty } = useProperty();
  const selectedHomeId = currentProperty?.id;

  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [overdueCollapsed, setOverdueCollapsed] = useState(false);
  const [upcomingCollapsed, setUpcomingCollapsed] = useState(false);
  const [snoozedCollapsed, setSnoozedCollapsed] = useState(false);
  const [completedCollapsed, setCompletedCollapsed] = useState(true);
  const [skippedCollapsed, setSkippedCollapsed] = useState(true);

  // Skip modal state
  const [skipTaskId, setSkipTaskId] = useState<string | null>(null);
  const skipTask = tasks.find((t) => t.id === skipTaskId);

  // DIY drawer state (old flow — DIYMaintenanceDrawer)
  const [diyTask, setDiyTask] = useState<ScheduledTask | null>(null);

  // Order Parts state (old flow — AIDrawer with parts mode)
  const [partsTask, setPartsTask] = useState<ScheduledTask | null>(null);

  // ─── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!selectedHomeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getMaintenanceSchedule(selectedHomeId);
      setTasks(data);
    } catch {
      setError(t('error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [selectedHomeId]);

  useEffect(() => {
    load();
  }, [load]);

  // ─── Actions ───────────────────────────────────────────────────────────────
  const handleDone = useCallback(async (taskId: string, performedAt: string) => {
    try {
      const updated = await updateScheduledTask(taskId, { status: 'completed', performedAt });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
      const nextDue = new Date(updated.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      toast.success(t('toast.taskDone', { date: nextDue }));
    } catch {
      toast.error(t('toast.taskDoneError'));
    }
  }, []);

  const handleSnooze = useCallback(async (taskId: string, snoozedUntil: string) => {
    try {
      const updated = await updateScheduledTask(taskId, { status: 'snoozed', snoozedUntil });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
      // Reset filter to 'all' and expand the snoozed section so the task is visible.
      // Without this, an active filter (e.g. 'upcoming') would keep count=0 and the
      // section returns null, making the task appear to vanish.
      setActiveFilter('all');
      setSnoozedCollapsed(false);
      const untilDate = new Date(snoozedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      toast.success(t('toast.snoozed', { date: untilDate }));
    } catch {
      toast.error(t('toast.snoozeError'));
    }
  }, []);

  const handleSkipConfirm = useCallback(async (reason: SkipReasonValue) => {
    if (!skipTaskId) return;
    try {
      const updated = await updateScheduledTask(skipTaskId, {
        status: 'skipped',
        skipReason: reason,
      });
      setTasks((prev) => prev.map((t) => (t.id === skipTaskId ? updated : t)));
      setSkipTaskId(null);
      // Reset filter to 'all' and expand the Skipped section so the task
      // is immediately visible (section starts collapsed by default).
      setActiveFilter('all');
      setSkippedCollapsed(false);
      toast.success(t('toast.skipped'));
    } catch {
      toast.error(t('toast.skipError'));
    }
  }, [skipTaskId]);

  const handleDIY = useCallback((task: ScheduledTask) => setDiyTask(task), []);

  const handleOrderParts = useCallback((task: ScheduledTask) => setPartsTask(task), []);

  // ─── Filtered tasks ────────────────────────────────────────────────────────
  const filtered = tasks.filter((t) => {
    const matchesSearch =
      !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.assetName.toLowerCase().includes(search.toLowerCase());

    const matchesFilter =
      activeFilter === 'all' || t.status === activeFilter;

    return matchesSearch && matchesFilter;
  });

  const groups = groupByStatus(filtered);
  const stats = groupByStatus(tasks); // unfiltered for summary cards

  const totalActive = (stats.overdue.length + stats.upcoming.length);

  const subtitle = (() => {
    if (totalActive > 0) return t('subtitle.attention', { count: totalActive });
    if (tasks.length === 0) return t('subtitle.noTasks');
    return t('subtitle.allGood');
  })();

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">

      {/* ── Hero header ─────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          <div className="flex items-end justify-between">
            <div>
              <h1 className={getHeroClass('main')}>{t('title')}</h1>
              <p className="text-muted-foreground">{subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8 space-y-6">

        {/* ── Error ───────────────────────────────────────────────────── */}
        {error && (
          <div className="flex items-center gap-3 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="ml-auto shrink-0 text-red-600 border-red-300"
            >
              {t('button.retry')}
            </Button>
          </div>
        )}

        {/* ── Summary cards ───────────────────────────────────────────── */}
        {tasks.length > 0 && (
          <MaintenanceSummaryCards
            overdue={stats.overdue.length}
            upcoming={stats.upcoming.length}
            completed={stats.completed.length}
            snoozed={stats.snoozed.length}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />
        )}

        {/* ── Search + filter bar ─────────────────────────────────────── */}
        {tasks.length > 0 && (
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('search')}
                className="pl-9"
              />
            </div>
            {/* Quick filter pills */}
            <div className="flex items-center gap-1.5">
              {['all', 'overdue', 'upcoming', 'snoozed'].map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(activeFilter === f ? 'all' : f)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-semibold capitalize transition-colors',
                    activeFilter === f
                      ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900'
                      : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700',
                  )}
                >
                  {t(`filter.${f}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Empty state ─────────────────────────────────────────────── */}
        {tasks.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div className="h-16 w-16 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
              <Wrench className="h-7 w-7 text-zinc-400" />
            </div>
            <div>
              <p className="text-base font-semibold text-zinc-700 dark:text-zinc-300">
                {t('empty.title')}
              </p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                {t('empty.description')}
              </p>
            </div>
          </div>
        )}

        {/* ── Sections ────────────────────────────────────────────────── */}
        {tasks.length > 0 && (
          <div>
            {/* Overdue */}
            <MaintenanceSection
              title={t('section.overdue')}
              icon={AlertCircle}
              iconColor="text-red-600 dark:text-red-400"
              headerBg="bg-red-50 dark:bg-red-950/20"
              count={groups.overdue.length}
              tasks={groups.overdue}
              collapsed={overdueCollapsed}
              onToggle={() => setOverdueCollapsed((v) => !v)}
              onDone={handleDone}
              onSkip={setSkipTaskId}
              onSnooze={handleSnooze}
              onDIY={handleDIY}
              onOrderParts={handleOrderParts}
            />

            {/* Upcoming */}
            <MaintenanceSection
              title={t('section.upcoming')}
              icon={CheckCircle2}
              iconColor="text-[hsl(var(--brand-primary))]"
              headerBg="bg-[hsl(var(--brand-primary)/0.06)] dark:bg-[hsl(var(--brand-primary)/0.12)]"
              count={groups.upcoming.length}
              tasks={groups.upcoming}
              collapsed={upcomingCollapsed}
              onToggle={() => setUpcomingCollapsed((v) => !v)}
              onDone={handleDone}
              onSkip={setSkipTaskId}
              onSnooze={handleSnooze}
              onDIY={handleDIY}
              onOrderParts={handleOrderParts}
            />

            {/* Snoozed */}
            <MaintenanceSection
              title={t('section.snoozed')}
              icon={History}
              iconColor="text-zinc-500 dark:text-zinc-400"
              headerBg="bg-zinc-50 dark:bg-zinc-900"
              count={groups.snoozed.length}
              tasks={groups.snoozed}
              collapsed={snoozedCollapsed}
              onToggle={() => setSnoozedCollapsed((v) => !v)}
              onDone={handleDone}
              onSkip={setSkipTaskId}
              onSnooze={handleSnooze}
              onDIY={handleDIY}
              onOrderParts={handleOrderParts}
            />

            {/* Completed — collapsible */}
            {groups.completed.length > 0 && (
              <MaintenanceSection
                title={t('section.completed')}
                icon={CheckCircle2}
                iconColor="text-green-600 dark:text-green-400"
                headerBg="bg-green-50 dark:bg-green-950/20"
                count={groups.completed.length}
                tasks={groups.completed}
                collapsed={completedCollapsed}
                onToggle={() => setCompletedCollapsed((v) => !v)}
                onDone={handleDone}
                onSkip={setSkipTaskId}
                onSnooze={handleSnooze}
                onDIY={handleDIY}
                onOrderParts={handleOrderParts}
              />
            )}

            {/* Skipped — collapsible */}
            {groups.skipped.length > 0 && (
              <MaintenanceSection
                title={t('section.skipped')}
                icon={XCircle}
                iconColor="text-amber-600 dark:text-amber-400"
                headerBg="bg-amber-50 dark:bg-amber-950/20"
                count={groups.skipped.length}
                tasks={groups.skipped}
                collapsed={skippedCollapsed}
                onToggle={() => setSkippedCollapsed((v) => !v)}
                onDone={handleDone}
                onSkip={setSkipTaskId}
                onSnooze={handleSnooze}
                onDIY={handleDIY}
                onOrderParts={handleOrderParts}
              />
            )}

            {/* All filtered out state */}
            {filtered.length === 0 && tasks.length > 0 && (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
                <Search className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
                <p className="text-sm text-muted-foreground">{t('noResults.message')}</p>
                <button
                  onClick={() => { setSearch(''); setActiveFilter('all'); }}
                  className="text-xs font-medium text-[hsl(var(--brand-primary))] hover:underline"
                >
                  {t('noResults.clearFilters')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Skip Reason Modal ──────────────────────────────────────────── */}
      {skipTask && (
        <SkipReasonModal
          taskTitle={skipTask.title}
          onConfirm={handleSkipConfirm}
          onClose={() => setSkipTaskId(null)}
        />
      )}

      {/* ── DIY Guide Drawer ───────────────────────────────────────────── */}
      {/* Reuse diyStepsJson cached on the task (returned by the initial */}
      {/* /maintenance/schedule call) so we skip the OpenAI round-trip.   */}
      {diyTask && (
        <DIYMaintenanceDrawer
          maintenanceTitle={diyTask.title}
          assetName={diyTask.assetName}
          assetLocation={diyTask.assetLocation ?? null}
          preloadedSteps={diyTask.diyStepsJson}
          preloadedParts={diyTask.partsJson}
          preloadedDifficulty={diyTask.difficultyLevel}
          preloadedDurationMinutes={diyTask.estimatedMinutes ?? null}
          estimatedCost={diyTask.estimatedCost ?? null}
          diyAvailable={diyTask.diyAvailable ?? true}
          onClose={() => setDiyTask(null)}
          onComplete={() => {
            handleDone(diyTask.id, new Date().toISOString());
            setDiyTask(null);
          }}
        />
      )}

      {/* ── Order Parts (AI Drawer) ────────────────────────────────────── */}
      {/* Same cached-first pattern: pass partsJson from the schedule call. */}
      {partsTask && selectedHomeId && (
        <AIDrawer
          assetId={partsTask.assetId}
          homeId={selectedHomeId}
          assetName={partsTask.assetName}
          directMode="parts"
          maintenanceTask={partsTask.title}
          preloadedTaskParts={partsTask.partsJson}
          onClose={() => setPartsTask(null)}
        />
      )}
    </div>
  );
}
